import { useState, useRef, useEffect } from 'react';
import { useAdminStore } from '../store/adminStore';
import { GamePackage, Track } from '../types';
import { uploadTrack } from '../services/trackService';
import './AdminPanel.css';

/**
 * Панель администратора для управления пакетами игр
 * Позволяет создавать, редактировать и удалять пакеты и треки
 */
export const AdminPanel = () => {
  const { packages, addPackage, updatePackage, deletePackage } = useAdminStore();
  const [editingPackage, setEditingPackage] = useState<GamePackage | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [playingTrackIndex, setPlayingTrackIndex] = useState<number | null>(null);
  /** Длительность файла, чтобы ползунок старта не упирался в 60 сек. */
  const [durations, setDurations] = useState<Record<string, number>>({});
  /** Единственный аудиоэлемент превью в редакторе пакета. */
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playGenRef = useRef(0);

  /** Возвращает плеер превью. */
  const getPreviewAudio = (): HTMLAudioElement => {
    if (!audioRef.current) {
      const audio = new Audio();
      audio.preload = 'metadata';
      audioRef.current = audio;
    }
    return audioRef.current;
  };

  /** Останавливает превью и сбрасывает источник. */
  const releasePreview = () => {
    playGenRef.current += 1;
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
  };

  useEffect(() => {
    return () => {
      releasePreview();
      audioRef.current = null;
    };
  }, []);

  const handleAddPackage = () => {
    const newPackage: GamePackage = {
      id: `package-${Date.now()}`,
      name: 'Новый пакет',
      description: '',
      tracks: [],
      settings: {
        defaultPlayDuration: 15,
        infoDisplayDelay: 5,
      },
    };
    setEditingPackage(newPackage);
    setIsAdding(true);
  };

  const handleEditPackage = (pkg: GamePackage) => {
    setEditingPackage({ ...pkg });
    setIsAdding(false);
  };

  const handleSavePackage = async () => {
    if (!editingPackage) return;

    // Проверка валидности треков
    const invalidTracks = editingPackage.tracks.filter(
      (track) => !track.artist || !track.title || !track.file
    );

    if (invalidTracks.length > 0) {
      alert(
        `Пожалуйста, заполните все поля (Исполнитель, Название и файл) для всех треков. Не заполнено треков: ${invalidTracks.length}`
      );
      return;
    }

    try {
      if (isAdding) {
        await addPackage(editingPackage);
      } else {
        await updatePackage(editingPackage.id, editingPackage);
      }

      releasePreview();
      setPlayingTrackIndex(null);
      setEditingPackage(null);
      setIsAdding(false);
    } catch (error) {
      console.error('Ошибка сохранения пакета:', error);
      alert('Ошибка сохранения пакета. Проверьте консоль для деталей.');
    }
  };

  const handleDeletePackage = async (id: string) => {
    if (confirm('Вы уверены, что хотите удалить этот пакет?')) {
      try {
        await deletePackage(id);
      } catch (error) {
        console.error('Ошибка удаления пакета:', error);
        alert('Ошибка удаления пакета. Проверьте консоль для деталей.');
      }
    }
  };

  const handleAddTrack = () => {
    if (!editingPackage) return;

    const newTrack: Track = {
      number: editingPackage.tracks.length + 1,
      file: '',
      artist: '',
      title: '',
      startTime: 0,
    };

    setEditingPackage({
      ...editingPackage,
      tracks: [newTrack, ...editingPackage.tracks],
    });
    if (playingTrackIndex !== null) {
      setPlayingTrackIndex(playingTrackIndex + 1);
    }
  };

  const handleUploadTrack = async (index: number, file: File) => {
    if (!editingPackage) return;

    try {
      const result = await uploadTrack(file);
      if (playingTrackIndex === index) {
        releasePreview();
        setPlayingTrackIndex(null);
      }
      const newTracks = [...editingPackage.tracks];
      const currentTrack = newTracks[index];
      
      // Предзаполняем поля только если они пустые
      newTracks[index] = { 
        ...currentTrack, 
        file: result.filename,
        startTime: currentTrack.startTime || 0,
        // Заполняем artist только если поле пустое и есть данные из файла
        artist: currentTrack.artist || result.artist || '',
        // Заполняем title только если поле пустое и есть данные из файла
        title: currentTrack.title || result.title || ''
      };

      setEditingPackage({
        ...editingPackage,
        tracks: newTracks,
      });
    } catch (error) {
      console.error('Ошибка загрузки файла:', error);
      alert('Ошибка загрузки файла. Проверьте консоль для деталей.');
    }
  };

  /**
   * Обработка множественной загрузки файлов
   * Создает строки для каждого файла и заполняет метаданные
   */
  const handleMultipleFileUpload = async (files: FileList) => {
    if (!editingPackage) return;

    const audioFiles = Array.from(files).filter((file) =>
      file.type.startsWith('audio/')
    );

    if (audioFiles.length === 0) {
      alert('Пожалуйста, выберите аудио файлы');
      return;
    }

    // Создаем новые треки для каждого файла
    const newTracks: Track[] = [];
    let currentNumber = editingPackage.tracks.length + 1;

    // Сначала создаем пустые треки
    for (let i = 0; i < audioFiles.length; i++) {
      newTracks.push({
        number: currentNumber + i,
        file: '',
        artist: '',
        title: '',
        startTime: 0,
      });
    }

    // Добавляем новые треки в начало списка
    const startIndex = 0; // Индекс начала новых треков
    setEditingPackage({
      ...editingPackage,
      tracks: [...newTracks, ...editingPackage.tracks],
    });
    if (playingTrackIndex !== null) {
      setPlayingTrackIndex(playingTrackIndex + newTracks.length);
    }

    // Загружаем файлы последовательно и заполняем метаданные
    for (let i = 0; i < audioFiles.length; i++) {
      try {
        const result = await uploadTrack(audioFiles[i]);
        const trackIndex = startIndex + i; // Индекс в начале списка

        setEditingPackage((prev) => {
          if (!prev) return prev;
          const tracks = [...prev.tracks];
          // Проверяем, что трек еще существует и не был удален
          if (tracks[trackIndex] && !tracks[trackIndex].file) {
            tracks[trackIndex] = {
              ...tracks[trackIndex],
              file: result.filename,
              artist: result.artist || '',
              title: result.title || '',
            };
          }
          return { ...prev, tracks };
        });
      } catch (error) {
        console.error(`Ошибка загрузки файла ${audioFiles[i].name}:`, error);
        alert(`Ошибка загрузки файла ${audioFiles[i].name}. Файл не был добавлен.`);
        // Удаляем трек, если загрузка не удалась
        setEditingPackage((prev) => {
          if (!prev) return prev;
          const tracks = [...prev.tracks];
          const trackIndex = startIndex + i;
          // Удаляем только если это пустой трек (без файла)
          if (tracks[trackIndex] && !tracks[trackIndex].file) {
            return {
              ...prev,
              tracks: tracks.filter((_, idx) => idx !== trackIndex),
            };
          }
          return prev;
        });
      }
    }
  };

  /**
   * Обработчик выбора множественных файлов через input
   */
  const handleMultipleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleMultipleFileUpload(files);
    }
    // Сбрасываем значение input, чтобы можно было выбрать те же файлы снова
    e.target.value = '';
  };

  const handleStartTimeChange = (index: number, newStartTime: number) => {
    if (!editingPackage) return;

    const newTracks = [...editingPackage.tracks];
    newTracks[index] = { ...newTracks[index], startTime: newStartTime };

    setEditingPackage({
      ...editingPackage,
      tracks: newTracks,
    });
  };

  /** Включает прослушивание трека с выбранной секунды. */
  const handlePlayFromPosition = (index: number, startTime: number) => {
    if (!editingPackage) return;

    const track = editingPackage.tracks[index];
    if (!track || !track.file) return;

    const audioUrl = `/tracks/${track.file}`;
    const audio = getPreviewAudio();
    const gen = (playGenRef.current += 1);

    audio.onended = () => {
      if (playGenRef.current === gen) setPlayingTrackIndex(null);
    };
    audio.onerror = () => {
      console.error('Ошибка воспроизведения трека');
      if (playGenRef.current === gen) setPlayingTrackIndex(null);
    };

    const seekAndPlay = () => {
      if (playGenRef.current !== gen) return;
      const duration = audio.duration;
      if (duration && Number.isFinite(duration)) {
        setDurations((prev) => ({ ...prev, [track.file]: duration }));
      }
      audio.currentTime = startTime;
      audio.play().catch((error) => {
        console.error('Ошибка воспроизведения трека:', error);
        if (playGenRef.current === gen) setPlayingTrackIndex(null);
      });
    };

    setPlayingTrackIndex(index);

    if (audio.src.endsWith(track.file) && audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
      seekAndPlay();
      return;
    }

    audio.onloadedmetadata = () => {
      audio.onloadedmetadata = null;
      seekAndPlay();
    };
    audio.src = audioUrl;
    audio.load();
  };

  /** Останавливает текущее превью. */
  const handleStopPlayback = () => {
    releasePreview();
    setPlayingTrackIndex(null);
  };

  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragCounterRef = useRef<Map<number, number>>(new Map());

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    const counter = dragCounterRef.current.get(index) || 0;
    dragCounterRef.current.set(index, counter + 1);
    
    if (counter === 0) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    const counter = (dragCounterRef.current.get(index) || 0) - 1;
    dragCounterRef.current.set(index, Math.max(0, counter));
    
    if (counter === 0) {
      setDragOverIndex(null);
    }
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    // Поддерживаем состояние при dragOver
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = async (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);
    dragCounterRef.current.set(index, 0);

    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('audio/')) {
      await handleUploadTrack(index, file);
    } else {
      alert('Пожалуйста, перетащите аудио файл');
    }
  };

  const handleFileSelect = async (index: number, file: File | null) => {
    if (file) {
      await handleUploadTrack(index, file);
    }
  };

  const handleUpdateTrack = (index: number, updates: Partial<Track>) => {
    if (!editingPackage) return;

    const newTracks = [...editingPackage.tracks];
    newTracks[index] = { ...newTracks[index], ...updates };

    setEditingPackage({
      ...editingPackage,
      tracks: newTracks,
    });
  };

  const handleDeleteTrack = (index: number) => {
    if (!editingPackage) return;
    if (playingTrackIndex === index) {
      handleStopPlayback();
    } else if (playingTrackIndex !== null && index < playingTrackIndex) {
      setPlayingTrackIndex(playingTrackIndex - 1);
    }
    const newTracks = editingPackage.tracks.filter((_, i) => i !== index);
    setEditingPackage({
      ...editingPackage,
      tracks: newTracks,
    });
  };

  if (editingPackage) {
    return (
      <div className="admin-panel">
        <div className="admin-panel-header">
          <h2>{isAdding ? 'Добавление пакета' : 'Редактирование пакета'}</h2>
          <div className="admin-panel-actions">
            <button
              className="admin-button admin-button-save"
              onClick={handleSavePackage}
            >
              Сохранить
            </button>
            <button
              className="admin-button admin-button-cancel"
              onClick={() => {
                handleStopPlayback();
                setEditingPackage(null);
                setIsAdding(false);
              }}
            >
              Отмена
            </button>
          </div>
        </div>

        <div className="admin-package-form">
          <div className="admin-form-field">
            <label>Название пакета:</label>
            <input
              type="text"
              value={editingPackage.name}
              onChange={(e) =>
                setEditingPackage({ ...editingPackage, name: e.target.value })
              }
            />
          </div>

          <div className="admin-form-field">
            <label>Описание:</label>
            <textarea
              value={editingPackage.description}
              onChange={(e) =>
                setEditingPackage({
                  ...editingPackage,
                  description: e.target.value,
                })
              }
              rows={3}
            />
          </div>

          <div className="admin-settings-section">
            <h3>Настройки игры</h3>
            <div className="admin-settings-grid">
              <label className="admin-form-field">
                Отрывок, сек
                <input
                  type="number"
                  min="5"
                  max="60"
                  value={editingPackage.settings?.defaultPlayDuration || 15}
                  onChange={(e) =>
                    setEditingPackage({
                      ...editingPackage,
                      settings: {
                        ...editingPackage.settings,
                        defaultPlayDuration: parseInt(e.target.value, 10) || 15,
                        infoDisplayDelay: editingPackage.settings?.infoDisplayDelay || 5,
                      },
                    })
                  }
                />
              </label>
              <label className="admin-form-field">
                Инфо через, сек
                <input
                  type="number"
                  min="0"
                  max="30"
                  value={editingPackage.settings?.infoDisplayDelay || 5}
                  onChange={(e) =>
                    setEditingPackage({
                      ...editingPackage,
                      settings: {
                        ...editingPackage.settings,
                        defaultPlayDuration: editingPackage.settings?.defaultPlayDuration || 15,
                        infoDisplayDelay: parseInt(e.target.value, 10) || 5,
                      },
                    })
                  }
                />
              </label>
            </div>
          </div>

          <div className="admin-tracks-section">
            <div className="admin-tracks-header">
              <h3>Треки ({editingPackage.tracks.length})</h3>
              <div className="admin-tracks-header-buttons">
                <label className="admin-button admin-button-add admin-button-upload">
                  Загрузить файлы
                  <input
                    type="file"
                    multiple
                    accept="audio/*"
                    onChange={handleMultipleFileSelect}
                    style={{ display: 'none' }}
                  />
                </label>
                <button
                  className="admin-button admin-button-add"
                  onClick={handleAddTrack}
                >
                  + Трек
                </button>
              </div>
            </div>

            <div className="admin-tracks-list">
              {editingPackage.tracks.map((track, index) => {
                const isPlaying = playingTrackIndex === index;
                const sliderMax = Math.max(
                  durations[track.file] || 60,
                  (track.startTime || 0) + 5
                );
                return (
                  <div
                    key={index}
                    className={`admin-track-item ${dragOverIndex === index ? 'drag-over' : ''} ${track.file ? 'has-file' : ''}`}
                    onDragEnter={(e) => handleDragEnter(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={(e) => handleDragLeave(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                  >
                    <span className="admin-track-number">{index + 1}</span>
                    <input
                      type="text"
                      placeholder="Исполнитель"
                      value={track.artist}
                      onChange={(e) => handleUpdateTrack(index, { artist: e.target.value })}
                      className="admin-track-input admin-track-artist"
                    />
                    <input
                      type="text"
                      placeholder="Название"
                      value={track.title}
                      onChange={(e) => handleUpdateTrack(index, { title: e.target.value })}
                      className="admin-track-input admin-track-title"
                    />
                    <label className="admin-track-start">
                      <input
                        type="number"
                        min="0"
                        max={sliderMax}
                        step="0.1"
                        value={Number((track.startTime || 0).toFixed(1))}
                        onChange={(e) => {
                          const newTime = parseFloat(e.target.value) || 0;
                          handleStartTimeChange(index, newTime);
                          if (isPlaying && audioRef.current) {
                            audioRef.current.currentTime = newTime;
                          }
                        }}
                        className="admin-track-start-num"
                        disabled={!track.file}
                      />
                      <span>с</span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max={sliderMax}
                      step="0.1"
                      value={track.startTime || 0}
                      onChange={(e) => {
                        const newTime = parseFloat(e.target.value);
                        handleStartTimeChange(index, newTime);
                        if (isPlaying && audioRef.current) {
                          audioRef.current.currentTime = newTime;
                        }
                      }}
                      className="admin-start-time-slider"
                      disabled={!track.file}
                    />
                    <button
                      type="button"
                      className={`admin-track-play ${isPlaying ? 'is-playing' : ''}`}
                      onClick={() =>
                        isPlaying
                          ? handleStopPlayback()
                          : handlePlayFromPosition(index, track.startTime || 0)
                      }
                      disabled={!track.file}
                      title={isPlaying ? 'Стоп' : 'Слушать с точки старта'}
                    >
                      {isPlaying ? '■' : '▶'}
                    </button>
                    <label className={`admin-track-file ${track.file ? 'has-file' : ''}`}>
                      {track.file ? 'файл' : 'загрузить'}
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={(e) => {
                          handleFileSelect(index, e.target.files?.[0] || null);
                          e.target.value = '';
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      className="admin-track-delete"
                      onClick={() => handleDeleteTrack(index)}
                      title="Удалить трек"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-panel-header">
        <h2>Управление пакетами игр</h2>
        <button
          className="admin-button admin-button-add"
          onClick={handleAddPackage}
        >
          + Добавить пакет
        </button>
      </div>

      <div className="admin-packages-list">
        {packages.length === 0 ? (
          <div className="admin-empty">
            <p>Нет пакетов. Создайте первый пакет!</p>
          </div>
        ) : (
          packages.map((pkg) => (
            <div key={pkg.id} className="admin-package-card">
              <div className="admin-package-info">
                <h3>{pkg.name}</h3>
                <p>{pkg.description || 'Нет описания'}</p>
                <div className="admin-package-stats">
                  Треков: {pkg.tracks.length}
                </div>
              </div>
              <div className="admin-package-actions">
                <button
                  className="admin-button admin-button-edit"
                  onClick={() => handleEditPackage(pkg)}
                >
                  Редактировать
                </button>
                <button
                  className="admin-button admin-button-delete"
                  onClick={() => handleDeletePackage(pkg.id)}
                >
                  Удалить
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

